// App Migration Direct Bottom Sheet Component
// Variant B: Opens the bottom sheet directly on page load without TopBanner or FloatingBanner
// Used when experiment variant is 'app-migration-b'

import React, { memo, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Helpers
import { useEligibleSSRVariantName } from 'shared/components/ExperimentV2/helpers';
import { log } from 'shared/helpers/newRelic';
import { TAG_IDENTIFIER } from 'shared/helpers/newRelic/constants';

// Hooks
import { useAppInstallDetection } from 'shared/components/AppMigration/hooks';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// Helpers
import {
  isBannerDismissed,
  dismissBanner,
  navigateToURL,
  getAppMigrationOneLinkUrl,
  isIOSDevice,
} from 'shared/components/AppMigration/helpers';
import {
  buildAppMigrationInteractionLocation,
  getAppMigrationAnalyticsPage,
} from 'shared/components/AppMigration/analytics';

// Constants
import {
  CTA_TEXT,
  APP_MIGRATION_EXP,
  APP_MIGRATION_EXP_VARIANT_B,
  STORE_URLS,
} from 'shared/components/AppMigration/constants';
// Components
import AppMigrationBottomSheet, {
  preloadBottomSheetImages,
} from 'shared/components/AppMigration/AppMigrationBottomSheet';

// Types
import { IAppMigrationBannerProps, BannerCTAText } from 'shared/components/AppMigration/types';

/**
 * AppMigrationDirectBottomSheet - Variant B of the app migration experiment
 *
 * Instead of showing a TopBanner + FloatingBanner flow, this variant
 * directly opens the BottomSheet on page load (once per session).
 *
 * Behavior:
 * - Opens bottom sheet automatically on first page load
 * - If user closes, it won't reopen until next session
 * - Respects the same dismiss logic as other banners (24-hour cooldown)
 * - No top banner or floating banner shown
 *
 * @param pageType - The page type for analytics tracking
 * @param onCTAClick - Callback when CTA is clicked
 */
function AppMigrationDirectBottomSheet({ onCTAClick }: IAppMigrationBannerProps) {
  const { search } = useLocation();
  const isSearchOpen = search.includes('showsearch');

  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const hasOpenedRef = useRef(false);

  const appsFlyerLinks = useRemoteConfig('appMigrationAppsFlyerLinks');

  // AB Experiment for feature flag
  const { isEligible = false, variant = '' } = useEligibleSSRVariantName(APP_MIGRATION_EXP);
  const isFeatureEnabled = isEligible && variant === APP_MIGRATION_EXP_VARIANT_B;

  // App installation detection
  const { isLoading: isDetecting, isMobile, platform, isAppInstalled } = useAppInstallDetection();

  // Auto-open bottom sheet on mount (once per session)
  useEffect(() => {
    // Wait for detection and experiment to resolve
    if (isDetecting || !isFeatureEnabled) return;

    // Only trigger once
    if (hasOpenedRef.current) return;

    // Only show on mobile web
    if (!isMobile) return;

    // Check if banner was dismissed recently (24hr cooldown)
    if (isBannerDismissed()) return;

    hasOpenedRef.current = true;

    // Preload images and open the bottom sheet
    preloadBottomSheetImages();

    // Small delay to let the page render first
    const timer = setTimeout(() => {
      setIsBottomSheetOpen(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [isDetecting, isMobile, isFeatureEnabled]);

  /**
   * Handle bottom sheet close
   */
  const handleBottomSheetClose = useCallback(() => {
    setIsBottomSheetOpen(false);
    // Dismiss so it doesn't reopen for 24 hours
    dismissBanner();
  }, []);

  /**
   * Handle download button click — uses remote-config OneLink
   */
  const handleDownload = useCallback(() => {
    const oneLinkUrl = getAppMigrationOneLinkUrl(appsFlyerLinks, 'bottomSheet');
    const url = oneLinkUrl || (isIOSDevice() ? STORE_URLS.APP_STORE : STORE_URLS.PLAY_STORE);

    if (!oneLinkUrl) {
      log({
        errTitle:
          '[AppMigration] Missing bottomSheet OneLink in remote config, using store fallback',
        tags: { [TAG_IDENTIFIER.MODULE]: 'AppMigrationDirectBottomSheet' },
      });
    }

    if (onCTAClick) {
      onCTAClick(url);
    }

    navigateToURL(url);
  }, [appsFlyerLinks, onCTAClick]);

  /**
   * Determine CTA text based on platform and app install status
   * - iOS: Always "Download Now"
   * - Android with app installed: "Use App"
   * - Android without app: "Download Now"
   */
  const getCTAText = useCallback((): BannerCTAText => {
    if (platform === 'ios') {
      return CTA_TEXT.DOWNLOAD_NOW;
    }
    if (platform === 'android' && isAppInstalled) {
      return CTA_TEXT.USE_APP;
    }
    return CTA_TEXT.DOWNLOAD_NOW;
  }, [platform, isAppInstalled]);

  // Don't render during SSR
  if (typeof window === 'undefined') return null;

  // Don't render when search is open
  if (isSearchOpen) return null;

  // Don't render if feature not enabled
  if (!isFeatureEnabled) return null;

  return (
    <AppMigrationBottomSheet
      isOpen={isBottomSheetOpen}
      onClose={handleBottomSheetClose}
      onDownload={handleDownload}
      analyticsInteractionLocation={buildAppMigrationInteractionLocation(
        getAppMigrationAnalyticsPage(),
        'banner'
      )}
      ctaText={getCTAText()}
    />
  );
}

export default memo(AppMigrationDirectBottomSheet);
